import {
  renderGradeColumn,
  renderHeaderTooltip
} from '@utils/assessmentReportUtils'
import { AssessmentItem } from 'src/types/type'

import { Link } from '@suntex.core/nav'
import {
  CellCheckBox,
  ColumnType,
  HeaderCheckBox,
  RowData,
  Tooltip
} from '@suntex.fim/ui'

export const renderWrappedAssessmentHeader = (label: string) => (
  <div className="assessment-report-wrapped-header">
    {renderHeaderTooltip(label)}
  </div>
)

export function generateCommonColumns<T extends AssessmentItem>(
  reportType?: 'jtf'
) {
  const commonColumns: ColumnType<T>[] = [
    {
      id: 'select',
      size: 40,
      enableResizing: false,
      enableSorting: false,
      header: HeaderCheckBox,
      cell: CellCheckBox
    },
    {
      id: 'userId',
      accessorKey: 'userId',
      header: () => renderHeaderTooltip('User ID'),
      size: 50,
      enableSorting: true,
      cell: (info) => {
        const userId = info.getValue() as string
        const message: string = info.row.original.isPrimaryTeam
          ? 'This team is the primary team for this player.'
          : 'This player is a guest on this team.'

        return (
          <Tooltip
            arrow={false}
            classNames={{ root: 'progress-tooltip' }}
            title={message}
          >
            {reportType === 'jtf' ? (
              <Link
                className="user-id-col"
                to={`./by-player?playerId=${info.row.original.playerId}`}
              >
                {userId}
              </Link>
            ) : (
              <b>{userId}</b>
            )}
          </Tooltip>
        )
      }
    },
    {
      header: () => renderHeaderTooltip('Display Name'),
      size: 50,
      accessorKey: 'displayName',
      cell: (info) => {
        return (info.getValue() as string)?.length > 0 ? (
          <b>{info.getValue() as string}</b>
        ) : (
          <b>None</b>
        )
      },
      id: 'displayName',
      enableSorting: true
    },
    {
      header: () => renderHeaderTooltip('Stickers'),
      accessorKey: 'stickers',
      cell: (info) => <b>{info.getValue<string>()?.toLocaleString()}</b>,
      id: 'stickers',
      size: 50,
      enableHiding: true,
      enableSorting: true
    }
  ]
  return commonColumns
}

export function generateScrollableAssessmentCommonColumns<
  T extends AssessmentItem
>(reportType?: 'jtf') {
  return generateCommonColumns<T>(reportType).map((column) => {
    if (column.id === 'userId') return { ...column, size: 120 }
    if (column.id === 'displayName') return { ...column, size: 160 }
    if (column.id === 'stickers') return { ...column, size: 120 }
    return column
  })
}

export function generateCommonColumnsForSchool<
  T extends RowData
>(): ColumnType<T>[] {
  return [
    {
      id: 'name',
      accessorKey: 'name',
      header: () => renderHeaderTooltip('Name'),
      cell: (info) => {
        return (info.getValue() as string)?.length > 0 ? (
          <b>{info.getValue() as string}</b>
        ) : (
          <b>None</b>
        )
      },
      size: 50,
      enableSorting: true
    },
    {
      header: () => renderHeaderTooltip('Class Name'),
      size: 50,
      accessorKey: 'desc',
      cell: (info) => {
        return (info.getValue() as string)?.length > 0 ? (
          <b>{info.getValue() as string}</b>
        ) : (
          <b>None</b>
        )
      },
      id: 'desc',
      enableSorting: true
    },
    {
      header: () => renderHeaderTooltip('Teacher'),
      size: 50,
      accessorKey: 'contactName',
      cell: (info) => {
        return (info.getValue() as string)?.length > 0 ? (
          <b>{info.getValue() as string}</b>
        ) : (
          <b>None</b>
        )
      },
      id: 'contactName',
      enableSorting: true
    },
    {
      header: () => renderHeaderTooltip('Grade'),
      size: 50,
      accessorKey: 'teamGrade',
      cell: (info: any) => {
        return renderGradeColumn(info.getValue())
      },
      id: 'teamGrade',
      enableSorting: true
    },
    {
      header: () => renderHeaderTooltip('Students'),
      size: 50,
      accessorKey: 'students',
      cell: (info) => {
        return info.getValue() ? (
          <b>{info.getValue<number>().toLocaleString()}</b>
        ) : (
          <b>0</b>
        )
      },
      id: 'students',
      enableSorting: true
    },
    {
      header: () => renderHeaderTooltip('Stickers'),
      accessorKey: 'stickers',
      cell: (info) => {
        return info.getValue() ? (
          <b>{info.getValue<number>().toLocaleString()}</b>
        ) : (
          <b>0</b>
        )
      },
      id: 'stickers',
      size: 50,
      enableHiding: true,
      enableSorting: true
    }
  ]
}

export function generateScrollableAssessmentCommonColumnsForSchool<
  T extends RowData
>(): ColumnType<T>[] {
  return generateCommonColumnsForSchool<T>().map((column) => {
    if (column.id === 'name') return { ...column, size: 140 }
    if (column.id === 'desc' || column.id === 'contactName')
      return { ...column, size: 160 }
    if (column.id === 'teamGrade' || column.id === 'students')
      return { ...column, size: 110 }
    if (column.id === 'stickers') return { ...column, size: 120 }
    return column
  })
}
